import React, { useEffect, useRef, useState } from 'react'
import { ImagePicker } from './resourcepicker'
import { resources as resourcesApi } from '../../lib/api'
import { GRID, WORK_SIDE, cellEdge, measureTarget, rateTarget } from '../../lib/targetAnalysis'
import { t } from 'stores/i18n.store'

const EXTENSION_TYPES = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' }
// Couleur de chaque verdict, du vert au rouge (bordure de l'aperçu et fond de l'étiquette), et couleur du texte sur ce fond.
const VERDICT_STYLES = {
  excellent: { color: '#13793a', text: '#fff' },
  good: { color: '#8cc63f', text: '#1f2d0f' },
  average: { color: '#f2c12e', text: '#3d2f00' },
  poor: { color: '#f07d22', text: '#3b1a00' },
  bad: { color: '#d7191c', text: '#fff' }
}

const isResourceId = value => typeof value === 'string' && !!value && !value.startsWith('__transfert__')

const decodeImage = url =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = url
  })

// Mesure le fichier que l'application télécharge (hd, sinon l'original), ramené à la taille de travail sur fond blanc.
const loadAndMeasure = async uid => {
  const resource = await resourcesApi.get(uid)
  const file = (resource.hd && resource.hd.file) || resource.original.file
  const response = await fetch(resourcesApi.getUrl(file))
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  try {
    const image = await decodeImage(url)
    const scale = Math.min(1, WORK_SIDE / Math.max(image.naturalWidth, image.naturalHeight))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))
    const context = canvas.getContext('2d')
    context.fillStyle = '#fff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.imageSmoothingQuality = 'high'
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    const { data } = context.getImageData(0, 0, canvas.width, canvas.height)
    const mimeType = blob.type.startsWith('image/')
      ? blob.type
      : EXTENSION_TYPES[
          file
            .split('.')
            .pop()
            .toLowerCase()
        ]
    return measureTarget(
      { rgba: data, width: canvas.width, height: canvas.height },
      { width: image.naturalWidth, height: image.naturalHeight, mimeType }
    )
  } finally {
    URL.revokeObjectURL(url)
  }
}

// Mesures par ressource pour la session : une cible se mesure une fois, pour elle comme pour ses voisines. Un échec s'oublie,
// pour être retenté.
const measuresCache = new Map()
const measureResource = uid => {
  if (!measuresCache.has(uid)) {
    const promise = loadAndMeasure(uid)
    promise.catch(() => measuresCache.delete(uid))
    measuresCache.set(uid, promise)
  }
  return measuresCache.get(uid)
}

// Les autres cibles du module : les valeurs au bout de [path] dans les données du formulaire, les tableaux parcourus — une par
// élément, d'où leur numéro —, sauf celle du champ, repérée par son rang dans l'identifiant rjsf (« root_targets_2_source_resource »).
const siblingsOf = (data, path, fieldId) => {
  const values = path
    .split('.')
    .reduce((current, key) => current.flatMap(v => (Array.isArray(v) ? v : [v])).map(v => v && v[key]), [data])
  const ownIndex = Number((fieldId.match(/_(\d+)_/) || [])[1])
  return values.map((uid, i) => ({ uid, index: i + 1 })).filter(({ uid, index }) => index - 1 !== ownIndex && isResourceId(uid))
}

/** L'image en niveaux de gris que mesure l'analyse : sa zone utile encadrée, et les cases sans détail de cette zone voilées. */
const GrayPreview = ({ measures }) => {
  const canvas = useRef(null)

  useEffect(() => {
    const { gray, width, height, emptyCells, zone } = measures
    const context = canvas.current.getContext('2d')
    const image = context.createImageData(width, height)
    gray.forEach((value, i) => {
      image.data.fill(value, 4 * i, 4 * i + 3)
      image.data[4 * i + 3] = 255
    })
    context.putImageData(image, 0, 0)

    // Rectangle des cases [left, right[ × [top, bottom[ en pixels.
    const cellsRect = (left, top, right, bottom) => {
      const x = cellEdge(left, width)
      const y = cellEdge(top, height)
      return [x, y, cellEdge(right, width) - x, cellEdge(bottom, height) - y]
    }
    context.fillStyle = 'rgba(217, 83, 79, 0.45)'
    emptyCells.forEach((empty, i) => {
      const column = i % GRID
      const row = Math.floor(i / GRID)
      const inZone = column >= zone.left && column < zone.right && row >= zone.top && row < zone.bottom
      if (empty && inZone) context.fillRect(...cellsRect(column, row, column + 1, row + 1))
    })
    context.strokeStyle = '#337ab7'
    context.lineWidth = 3
    context.strokeRect(...cellsRect(zone.left, zone.top, zone.right, zone.bottom))
  }, [measures])

  return <canvas ref={canvas} width={measures.width} height={measures.height} style={{ maxWidth: 200, maxHeight: 160 }} />
}

/** Détail de l'estimation : image en gris, note et verdict, alertes avec les points que chacune retire. */
const TargetDetails = ({ measures, rating }) => (
  <div className="panel panel-default" style={{ display: 'flex', gap: '1em', padding: '0.75em', width: 520, maxWidth: '90vw' }}>
    <GrayPreview measures={measures} />
    <div>
      <p>
        {t('target-check-title')}{' '}
        <span
          className="label"
          style={{ background: VERDICT_STYLES[rating.verdict].color, color: VERDICT_STYLES[rating.verdict].text }}
        >
          {rating.score}/100 · {t(`target-check-verdict-${rating.verdict}`)}
        </span>
      </p>
      {rating.alerts.length === 0 && <p className="text-success">{t('target-check-no-alert')}</p>}
      <ul className="list-unstyled" style={{ margin: 0 }}>
        {rating.alerts.map(alert => (
          <li key={alert.key + (alert.values.index || '')} className={alert.level === 'error' ? 'text-danger' : 'text-warning'}>
            <strong>−{alert.penalty}</strong> {t(`target-alert-${alert.key}`, alert.values)}
          </li>
        ))}
      </ul>
    </div>
  </div>
)

// Mesure la cible [uid] et ses [siblings], puis la note : { status: idle | loading | done | failed, measures, rating }.
const useTargetRating = (uid, siblings) => {
  const [state, setState] = useState({ status: 'idle' })
  const siblingsKey = siblings.map(s => `${s.index}:${s.uid}`).join(',')

  useEffect(() => {
    if (!uid) {
      setState({ status: 'idle' })
      return
    }
    let cancelled = false
    setState({ status: 'loading' })
    const others = siblings.map(s =>
      measureResource(s.uid).then(
        measures => ({ index: s.index, measures }),
        () => null
      )
    )
    Promise.all([measureResource(uid), ...others])
      .then(([measures, ...rest]) => {
        if (!cancelled) setState({ status: 'done', measures, rating: rateTarget(measures, rest.filter(Boolean)) })
      })
      .catch(() => !cancelled && setState({ status: 'failed' }))
    return () => {
      cancelled = true
    }
    // siblings est recréé à chaque rendu : sa clé en résume le contenu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, siblingsKey])

  return state
}

/**
 * Sélecteur d'une image cible de RA : l'aperçu de l'image choisie est bordé de la couleur du verdict de l'estimation, dont le
 * détail s'affiche au survol de l'image à la souris, ou en la touchant. `ui:options.siblings` donne le chemin des autres cibles du
 * module dans ses données (« targets.source.resource ») : une cible trop proche d'une autre est signalée.
 */
export const TargetPicker = props => {
  const { formData, formContext, idSchema, uiSchema } = props
  const path = ((uiSchema || {})['ui:options'] || {}).siblings
  const siblings = path && formContext && idSchema ? siblingsOf(formContext.formData, path, idSchema.$id) : []
  const state = useTargetRating(isResourceId(formData) ? formData : null, siblings)
  const frame = state.status === 'done' && {
    color: VERDICT_STYLES[state.rating.verdict].color,
    details: <TargetDetails measures={state.measures} rating={state.rating} />
  }

  return (
    <div>
      <ImagePicker {...props} frame={frame || undefined} />
      {state.status === 'failed' && <p className="help-block text-danger">{t('target-check-failed')}</p>}
    </div>
  )
}
